import { Notification } from '@prenuvo/shared-library';
import React, { createContext, useRef, useState } from 'react';
import { useToasts } from 'react-toast-notifications';

import {
  useAuth,
  useFilter,
  useSafetyQuestionsQuery,
  useStudiesQuery,
  useUpdatePrescriptionStatusMutation,
  useUpdateStudyStatus,
  useUsersQuery,
} from '../hooks';
import { Study, StudyStatus } from '../types';
import { sedativePrescriptionRequestedInSafetyQuestions } from '../utils';

export type StudiesContextType = {
  studies: Array<Study>;
  setStudies: (studies: Array<Study>, skipPageReset?: boolean) => void;
  fetchStudies: (filter: any) => any;
  updateReferralStatus: (studyId: string) => void;
  updateConsultationStatus: (studyId: string, scheduledStatus: boolean) => void;
  formatStudies: (studyResponse: Array<any>) => Promise<any[] | undefined>;
  setFormattedStudies: (studyResponse: Array<any>, addToList?: boolean) => void;
  studyLoading: boolean;
  safetyQuestions: any;
  setSafetyQuestions: (safetyQuestions: any) => void;
  skipPageResetRef: React.RefObject<boolean>;
  isPrescriptionRequested: (study: Study) => Promise<boolean>;
};

export const StudiesContext = createContext<StudiesContextType | undefined>(undefined);

const StudiesProvider = ({ children }: { children: React.ReactNode }) => {
  const { allowedLocations } = useAuth();
  const { dateRange, location } = useFilter();
  const { fetchStudies, loading: studyLoading } = useStudiesQuery({
    status: '',
    location: location.value,
    dateStart: `${dateRange.startDate?.unix()}` || '0',
    dateEnd: `${dateRange.endDate?.unix()}` || '0',
  });
  const [studies, setStudiesOnly] = useState<Array<Study>>([]);
  const [safetyQuestions, setSafetyQuestions] = useState<any>({});
  const skipPageResetRef = useRef<boolean>(true);

  const setStudies = (studies: Array<Study>, skipPageReset = false) => {
    skipPageResetRef.current = skipPageReset;
    setStudiesOnly(studies);
  };
  const [updateStudyStatus] = useUpdateStudyStatus();
  const [updatePrescriptionStatusMutation] = useUpdatePrescriptionStatusMutation();

  const { fetchUsers } = useUsersQuery();

  const getReferralStatus = (status: StudyStatus) => {
    if (!status.referred) {
      if (!status.referredTime) {
        return 'pending';
      }
      return 'referral denied';
    }
    if (status.referred) {
      return 'referred';
    }
  };

  const { fetchSafetyQuestions } = useSafetyQuestionsQuery([]);
  const fetchSafetyQuestionsForStudyIds = async (studyIds: Array<string>) => {
    const { data } = await fetchSafetyQuestions({
      context: {
        clientName: 'hi',
      },
      variables: {
        studyIds: studyIds,
        prescription: true,
      },
    });
    if (data && data.safetyQuestions && data.safetyQuestions.state) {
      const fetchedSafetyQuestions = data.safetyQuestions.state;
      setSafetyQuestions(fetchedSafetyQuestions);
    }
  };

  const formatStudies = async (studyResponse: Array<any>) => {
    fetchSafetyQuestionsForStudyIds(studyResponse.map((study: any) => study.studyID));

    const uniqueUserIds = Array.from(
      new Set(studyResponse.map((study: any) => study.patient.prenuvoID)),
    );

    if (uniqueUserIds.length) {
      const { data: usersResponse } = await fetchUsers({
        context: {
          clientName: 'pii',
        },
        variables: {
          userFilter: {
            userId: uniqueUserIds.join(','),
          },
        },
      });

      const users = usersResponse.searchUsers;

      return studyResponse.map((study: any) => {
        const scanLocation = allowedLocations.find((location) => location.code === study.location);
        const sku = scanLocation
          ? scanLocation.skus.find((sku) => sku.skuID === study.items.primary.skuID)
          : '';
        return {
          ...study,
          scanLocation,
          ...(sku && { scanShortName: sku.shortName }),
          ...(sku && { scanType: `${sku.shortName} - ${sku.skuID}` }),
          referralStatus: getReferralStatus(study.status),
          ...users.find(({ userId }: { userId: string }) => userId === study.patient.prenuvoID),
        };
      });
    }
  };

  const setFormattedStudies = async (studyResponse: Array<any>, addToList = false) => {
    const formattedStudies = await formatStudies(studyResponse);

    if (formattedStudies) {
      if (!addToList) {
        setStudies(formattedStudies);
      } else {
        setStudies([...studies, ...formattedStudies]);
      }
    }
  };

  const { addToast } = useToasts();

  const updateReferralStatus = (studyId: string) => {
    const studiesWithReplacedStudy = studies.filter((study) => study.studyID !== studyId);
    setStudies(studiesWithReplacedStudy, true);

    addToast(
      Notification.create(
        'Updated status',
        'Successfully updated status',
      ),
      {
        appearance: 'success',
      },
    );
  };

  const updateConsultationStatus = (studyId: string, scheduledStatus: boolean) => {
    updateStudyStatus({
      studyId: studyId,
      statusList: [
        {
          valueBoolean: scheduledStatus,
          key: 'patientConsultDone',
        },
      ],
    });

    const updatedStudies = studies.filter((study) => study.studyID !== studyId);
    setStudies(updatedStudies, true);
  };

  const updatePrescriptionStatus = async (studyId: string, prescriptionStatus: string) => {
    await updatePrescriptionStatusMutation({
      studyId,
      prescriptionStatus,
    });
    const updatedStudies = studies.map((study) =>
      study.studyID === studyId
        ? {
            ...study,
            practitioners: {
              ...study.practitioners,
              prescribing: {
                ...study.practitioners.prescribing,
                prescriptionStatus,
              },
            },
          }
        : study,
    );
    setStudies(updatedStudies);
  };
  const isPrescriptionRequested = async (study: Study) => {
    const isPrescriptionRequestedInSafetyQuestions = sedativePrescriptionRequestedInSafetyQuestions(
      safetyQuestions,
      study.studyID,
    );
    if (isPrescriptionRequestedInSafetyQuestions) {
      if (study.practitioners?.prescribing?.prescriptionStatus === 'not requested') {
        await updatePrescriptionStatus(study.studyID, 'pending');
      }
      return true;
    }
    if (study.practitioners?.prescribing?.prescriptionStatus !== 'not requested') {
      await updatePrescriptionStatus(study.studyID, 'not requested');
    }
    return false;
  };

  return (
    <StudiesContext.Provider
      value={{
        studies,
        setStudies,
        fetchStudies,
        updateReferralStatus,
        updateConsultationStatus,
        formatStudies,
        setFormattedStudies,
        studyLoading,
        safetyQuestions,
        setSafetyQuestions,
        skipPageResetRef,
        isPrescriptionRequested,
      }}
    >
      {children}
    </StudiesContext.Provider>
  );
};

export default StudiesProvider;
